/**
 * Pacific-centred map helpers (TAMK3-9).
 *
 * 1. Pacific-centring
 * The stock world GeoJSON is centred on 0° (Greenwich), which splits the
 * Pacific / Australia across the two edges. To re-centre on 180° we push
 * everything in the far west (the Atlantic / Americas) eastward by +360°:
 * anything west of the -30° cutoff gets +360, landing 180° in the middle.
 * Dots and coordinates also run through pacificLon() so they line up with the
 * shifted landmasses.
 *
 * 2. Greenland as one country
 * Applying that shift per-vertex would tear apart any single landmass whose own
 * polygon crosses the -30° seam. Greenland spans roughly -73° to -11°, so its
 * western half (< -30) would jump +360 while its eastern half (>= -30) stayed
 * put, smearing it across the top of the map. straddlesSeam() detects a feature
 * with a ring that genuinely crosses -30 (a short edge with vertices on both
 * sides) and shifts that whole feature east by +360 instead of vertex-by-vertex
 * — keeping it intact as a single shape.
 *
 * The "short edge" test matters: a country like Russia has vertices on both
 * sides of -30 too, but only because its Chukotka tip sits past the 180°
 * dateline in a SEPARATE ring — a ~358° gap, not a contiguous crossing. That
 * must go per-vertex (which reunites it at 180°), never whole-shifted.
 */

const PACIFIC_LON_CUTOFF = -30

/** Shift a single longitude into the Pacific-centred frame. */
export function pacificLon (lon: number): number {
  return lon < PACIFIC_LON_CUTOFF ? lon + 360 : lon
}

type Pt = number[]

/** Deep-copy a (possibly deeply nested) coordinate array. */
function cloneCoords (c: any): any {
  return typeof c[0] === 'number' ? (c as number[]).slice() : c.map(cloneCoords)
}

/** Apply `fn` to every [lon, lat] vertex nested anywhere inside `c` (mutates). */
function forEachVertex (c: any, fn: (pt: Pt) => void): void {
  if (typeof c[0] === 'number') fn(c as Pt)
  else (c as any[]).forEach((x) => forEachVertex(x, fn))
}

/**
 * True if a ring has an EDGE that crosses the -30 cutoff — adjacent vertices on
 * opposite sides of -30 with a short (< 180°) step. A wide step (> 180°) is a
 * dateline gap, not a real crossing, and is deliberately excluded.
 */
function ringStraddlesSeam (ring: Pt[]): boolean {
  for (let i = 1; i < ring.length; i++) {
    const a = ring[i - 1][0]
    const b = ring[i][0]
    if (Math.abs(b - a) < 180 &&
        (a < PACIFIC_LON_CUTOFF) !== (b < PACIFIC_LON_CUTOFF)) {
      return true
    }
  }
  return false
}

/** True if any ring of a Polygon / MultiPolygon geometry crosses the seam. */
function straddlesSeam (geometry: any): boolean {
  if (geometry.type === 'Polygon') {
    return (geometry.coordinates as Pt[][]).some(ringStraddlesSeam)
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as Pt[][][]).some((poly) => poly.some(ringStraddlesSeam))
  }
  return false
}

/**
 * Return a DEEP COPY of a GeoJSON FeatureCollection re-framed Pacific-centred.
 * Coordinates are deep-copied so the source geometry — shared by reference with
 * the Atlantic-centred `world` map registration — is never mutated.
 */
export function toPacificGeoJson<T extends { features: any[] }> (fc: T): T {
  return {
    ...fc,
    features: fc.features.map((f: any) => {
      const g = f.geometry
      if (!g || !g.coordinates) return { ...f }
      const coords = cloneCoords(g.coordinates)
      if (straddlesSeam(g)) {
        // Move the whole landmass east, keeping it intact (Greenland).
        forEachVertex(coords, (pt) => { pt[0] += 360 })
      } else {
        // Per-vertex is safe — no ring crosses -30 (dateline gaps reunite).
        forEachVertex(coords, (pt) => { pt[0] = pacificLon(pt[0]) })
      }
      return { ...f, geometry: { ...g, coordinates: coords } }
    }),
  } as T
}
